/**
 * `checkout-groups` — the canonical partition of a cart into checkout groups.
 *
 * ## Why this exists
 *
 * A checkout is scoped to ONE seller (single-seller invariant,
 * `.tondocs/payments/SINGLE_SELLER_INVARIANT_SPEC.md`), ONE currency, and —
 * since WS4 — ONE `shipping_mode`. The server enforces the third axis:
 * `lib/payments/resolve-checkout-intent.ts` fails a `CheckoutIntent` whose
 * lines disagree on `listings.shipping_mode` with
 * `reason: "mixed_shipping_mode"`.
 *
 * `shipping_mode` is a per-LISTING column, so one seller may legally publish
 * both `legacy` and `live_rate` listings and a buyer may put both in the cart.
 * Before this module the cart grouped on `sellerId:currencyCode` only, so that
 * buyer got ONE mixed group whose:
 *   - `deriveShippingMode` degraded to `'legacy'` → the live-rate sub-flow
 *     never mounted,
 *   - `calculateSellerShippingTotal` priced the live_rate line's shipping at 0
 *     (it has no `selectedShippingMethod`),
 *   - Pay button passed its gates anyway (live_rate items bypass the
 *     "shipping method selected" check),
 *   - and the server then rejected the whole thing at pay time.
 *
 * The fix is left-most: make the mixture **unrepresentable at the grouping
 * layer**. `shippingMode` joins the group key, so every group this module
 * emits is homogeneous by construction and every downstream consumer
 * (`deriveShippingMode`, cart math, the Pay gate, `buildCheckoutIntent`)
 * sees exactly what the server contract demands.
 *
 * ## Contract
 *
 * - Pure domain (`hex` §1): no React, no I/O, no globals. Generic over a
 *   minimal structural item so `lib/` never imports `components/`.
 * - Group order is first-seen order of the items, matching the previous
 *   object-literal insertion order the cart UI rendered in.
 * - `null`/`undefined` `shippingMode` normalises to `'legacy'` — a cart row
 *   that predates the WS4.4 enrichment must keep behaving exactly as it did.
 * - A blank `sellerId` falls back to `unknown-<listingId>`, preserving the
 *   pre-existing behaviour of both former grouping sites (they each hand-rolled
 *   the same fallback).
 *
 * @see .tondocs/cart/CART_ARCHITECTURE.md §5.2, §6 (per-seller checkout)
 * @see .tondocs/shippo/PLAN.md §D WS4 (per-seller routing by shipping_mode)
 * @see lib/checkout/derive-shipping-mode.ts — the defensive guard downstream
 * @see lib/payments/resolve-checkout-intent.ts — `mixed_shipping_mode` failure
 * @see .tondocs/skills/hex.md §1 (pure domain layer)
 * @see .tondocs/skills/type-driven-design.md §6 (single source of truth for a
 *      variant dimension — `ShippingMode` is imported, never re-declared)
 */

import type { ShippingMode } from "@/lib/checkout/derive-shipping-mode";
import { DEFAULT_CURRENCY } from "@/lib/currencies";

/**
 * Separator between the three key axes. Safe against collisions: seller ids
 * are uuids, currency codes are ISO-4217 alpha-3, and `ShippingMode` is a
 * closed string-literal union — none can contain `:`. The `unknown-<listingId>`
 * fallback is uuid-shaped too.
 */
const KEY_SEPARATOR = ":";

/**
 * Opaque-ish handle for one checkout group. NEVER parse it: every field it
 * encodes is also carried structurally on {@link CheckoutGroupRef}. Splitting
 * the key was a real consumer in `use-cart-calculations.ts` before this
 * module existed, and it silently broke the moment a third axis was added.
 */
export type CheckoutGroupKey = string;

/** The three axes that define one checkout. All three must be homogeneous. */
export interface CheckoutGroupIdentity {
  sellerId: string;
  currencyCode: string;
  shippingMode: ShippingMode;
}

/** {@link CheckoutGroupIdentity} plus its derived lookup key. */
export interface CheckoutGroupRef extends CheckoutGroupIdentity {
  key: CheckoutGroupKey;
}

/**
 * Minimal structural shape this module needs from a cart row. `CartItemData`
 * (components/cart/types.ts) is a structural supertype of it, so callers pass
 * their own rows straight in and keep their own element type via the generic.
 */
export interface CheckoutGroupItem {
  listingId: string;
  sellerId?: string | null;
  currency?: string | null;
  shippingMode?: ShippingMode | null;
}

export interface CheckoutGroup<T extends CheckoutGroupItem> extends CheckoutGroupRef {
  items: T[];
}

/**
 * Total function over the `shipping_mode` column: anything that is not the
 * explicit `'live_rate'` literal — `null`, `undefined`, an unenriched
 * optimistic cart row — is `'legacy'`. Mirrors the default policy documented
 * on `deriveShippingMode`, so the two agree by construction.
 */
export function normalizeShippingMode(mode: ShippingMode | null | undefined): ShippingMode {
  return mode === "live_rate" ? "live_rate" : "legacy";
}

/** Seller axis, with the historical `unknown-<listingId>` fallback. */
export function resolveGroupSellerId(item: CheckoutGroupItem): string {
  const sellerId = item.sellerId;
  return sellerId && sellerId.trim().length > 0 ? sellerId : `unknown-${item.listingId}`;
}

/** Currency axis, defaulting to the project-wide `DEFAULT_CURRENCY`. */
export function resolveGroupCurrencyCode(item: CheckoutGroupItem): string {
  return item.currency || DEFAULT_CURRENCY;
}

/** Identity → key. The ONLY place the key format is spelled out. */
export function buildCheckoutGroupKey(identity: CheckoutGroupIdentity): CheckoutGroupKey {
  return [identity.sellerId, identity.currencyCode, identity.shippingMode].join(KEY_SEPARATOR);
}

/** Item → the identity of the group it belongs to. */
export function resolveCheckoutGroupIdentity(item: CheckoutGroupItem): CheckoutGroupIdentity {
  return {
    sellerId: resolveGroupSellerId(item),
    currencyCode: resolveGroupCurrencyCode(item),
    shippingMode: normalizeShippingMode(item.shippingMode),
  };
}

/** Item → the ref (identity + key) of the group it belongs to. */
export function resolveCheckoutGroupRef(item: CheckoutGroupItem): CheckoutGroupRef {
  const identity = resolveCheckoutGroupIdentity(item);
  return { ...identity, key: buildCheckoutGroupKey(identity) };
}

/**
 * Partition cart rows into homogeneous checkout groups.
 *
 * Every emitted group satisfies the three invariants a `CheckoutIntent`
 * needs: one seller, one currency, one shipping mode. Order is first-seen.
 */
export function groupItemsForCheckout<T extends CheckoutGroupItem>(
  items: readonly T[],
): CheckoutGroup<T>[] {
  const groups = new Map<CheckoutGroupKey, CheckoutGroup<T>>();

  for (const item of items) {
    const ref = resolveCheckoutGroupRef(item);
    const existing = groups.get(ref.key);
    if (existing) {
      existing.items.push(item);
    } else {
      groups.set(ref.key, { ...ref, items: [item] });
    }
  }

  return Array.from(groups.values());
}

/**
 * How many distinct checkout groups a given seller was split into, keyed by
 * `sellerId`. The cart UI uses this to explain WHY one seller renders two
 * cards ("Ships separately") — without it a mode split reads as a
 * duplicate-render bug.
 */
export function countGroupsPerSeller(
  groups: readonly Pick<CheckoutGroupIdentity, "sellerId">[],
): Record<string, number> {
  const counts: Record<string, number> = {};
  for (const group of groups) {
    counts[group.sellerId] = (counts[group.sellerId] ?? 0) + 1;
  }
  return counts;
}
